import React, { useEffect, useState } from 'react'
import { Field, FieldArray, useFormikContext } from 'formik'
import classNames from 'classnames'
import { isEmpty, isEqual, isNil, isNull, isString, isUndefined, pick } from 'lodash'
import { IconCheck, IconPlus, IconX } from '@tabler/icons-react'

import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { Button } from '@/components/buttons/Button'
import { ErrorMessage } from '@/components/forms/ErrorMessage'
import { FormField } from '@/components/forms/FormField'
import { InteractiveTooltip } from '@/components/tooltips/InteractiveTooltip'
import { Pill } from '@/components/pills/Pill'
import Select from '@/components/forms/Select'
import { Table } from '@/components/tables/Table/Table'
import { TextInput } from '@/components/forms/TextInput'
import { dateFormatMoment } from '@/utils/constants'
import { formatTime } from '@/utils/helpers'
import {
  REPLACE_PATCH_FAILED,
  REPLACE_REMOVE_FAILED,
  aggregateDocuments,
  aggregateLastSynced,
  aggregateStatus,
} from '@/views/Organizations/components/ConnectionContentTable.helpers'

/**
 * The merged content table for a knowledge-base connection (QCER-1073): the
 * editable allowlist rows carrying their own ingest state — Documents, Last
 * synced, Status — so the configuration and what it actually ingested are one
 * table instead of two listing the same selectors. Injected into the provider
 * forms through AbstractForm's `raw`/`as` escape hatch; reads/writes
 * `values.allowlist` through Formik context.
 *
 * Row lifecycle: a DRAFT row (added but never saved) lives only in Formik —
 * its inputs edit form state, Save persists it, and removing it is a local
 * splice. A PERSISTED row is server truth and read-only in place: Remove runs
 * the provider's remove-and-purge operation (QCER-1024), and Edit is a
 * REPLACE — the new spelling is PATCHed in alongside the old entry, then the
 * old entry is removed through the same purge operation — so config and
 * ingested data can never drift apart. Both destructive paths sit behind
 * provider-worded confirmations. The one exception is an edit that touches
 * only `carried` columns (see below): that is a plain form change Save
 * PATCHes, since it changes what the entry's documents answer or how they
 * rank, never which documents exist.
 *
 * Rows are classified persisted/draft by ORIGIN, not value: the saved entries
 * seed the form as the first persistedRowCount rows, and rows only ever
 * append or drop — never reorder — so persisted rows are exactly the prefix
 * [0, persistedRowCount). Classifying by value would misread a draft typed to
 * match an existing entry as persisted.
 *
 * @param {number} props.connectionId persisted connection id; null = create mode
 * @param {Array} props.persistedAllowlist the server-backed entries seeding the form
 * @param {Object} props.syncState undefined | null | { rows, updated_at, mode }
 * @param {string} props.title section heading
 * @param {string} props.caption explanatory text under the heading
 * @param {Array} props.columns [{ name, header, placeholder, showError, blankLabel }]
 *   the entry's columns; blankLabel renders muted when the value is blank.
 *   Text inputs by default; `control: 'select'` opts a column into the shared
 *   Select (QCER-1089, for SharePoint's row Type) — it takes `options`,
 *   `ariaLabel`, and may take `onSelect: (row, value) => nextRow` to rewrite
 *   the WHOLE row when the choice invalidates a sibling field; its display
 *   cell shows the selected option's label. `placeholderOf: (row) => string`
 *   overrides `placeholder` per row, and `disabledWhen: (row) => boolean`
 *   greys out the control on rows the value does not apply to — the display
 *   cell then shows a dash whatever the row holds, so a value the row cannot
 *   carry is never presented as in force.
 *   `control: 'multiselect'` (QCER-1075, for the playbook scope) reads and
 *   writes an ARRAY of values through the shared Select's isMulti UX; it takes
 *   `options`, `ariaLabel`, a `blankLabel` for the empty display, and optional
 *   `loading`/`placeholder`/`disabled`, plus `placeholderOf`/`disabledWhen`
 *   like a text column. Its display cell names the selected options, or
 *   counts them while none resolve (options still loading, or a value whose
 *   option was deleted since).
 *   `ariaLabelOf: (row) => string` overrides `ariaLabel` per row, the way
 *   `placeholderOf` overrides `placeholder`, so the open row's control is
 *   named after the entry it belongs to.
 *   `note: (row, { persisted }) => node` renders under the read-only cell
 *   value (never on the open row), for a per-row hint or action; null for none.
 *   `carried: true` marks a column the entry CARRIES rather than one that
 *   names it (QCER-1075's playbook scope). Like every other column it is
 *   read-only until Edit opens the row (QCER-1123 — the live cell it started
 *   with invited accidental changes), but
 *   confirming an edit that changed ONLY carried values writes them back to
 *   the form for Save to PATCH, never into the replace flow: old and new
 *   share one removal identity, so a replace would PATCH in a duplicate and
 *   then purge both.
 *   `showError` renders the row schema's message for the column: under the
 *   control while the row is open (a draft's once the field was touched), and
 *   under the read-only cell for a STORED value that blocks Save, so the
 *   operator knows which row to open.
 * @param {React.ReactNode} props.notice shown above the table, e.g. a suggestion
 *   banner the provider renders; null for none
 * @param {boolean} props.locked disables the add button and every row's actions,
 *   while the provider writes the allowlist itself and no row may open underneath
 * @param {Object} props.blankRow the empty row appended by the add button
 * @param {string} props.addLabel add-row button label
 * @param {Object} props.entrySchema Yup schema for ONE row (persisted-edit validation)
 * @param {Function} props.sameEntry provider identity comparator for two entries
 * @param {Function} props.normalizeEntry canonicalizes a typed entry (trim,
 *   link-to-id extraction) before it becomes a replacement's new spelling.
 *   It must return the IDENTITY keys only: the confirm decides "replace, or
 *   write back in place" by comparing its output, so a `carried` key in it
 *   would send a scope-only edit through replace — a duplicate PATCHed in,
 *   then both purged.
 * @param {Function} props.syncRowsFor (entry, persistedEntries, syncRows) => the
 *   sync rows the entry answers for (each sync row attributed to exactly one entry)
 * @param {Object} props.errorLabels last_error code => { label, description }
 * @param {Function} props.rowLabel sync row => its scope label, for the
 *   last-synced tooltip breakdown
 * @param {Function} props.onEditingChange reports a persisted-row edit
 *   opening/closing, so the form can lock Save and arm the close guard
 * @param {Function} props.onRemoved awaited after a successful removal/replace (reload)
 * @param {string} props.draftRemoveLabel menu label for a draft row's removal
 * @param {Function} props.persistedRemoveLabel row => menu label for a persisted
 *   row's removal (wording must not overstate a deferred purge)
 * @param {Function} props.removeTargetOf row => the provider-shaped removal target
 * @param {Function} props.removeEntry target => Promise (removal thunk; null = failed)
 * @param {Function} props.survivesTarget (row, target) => whether a form row matches
 *   the removed identity
 * @param {Function} props.renderRemoveConfirmModal ({ target, removing, onConfirm,
 *   onClose }) => modal JSX
 * @param {Function} props.replaceEntry ({ oldEntry, newEntry }) => Promise resolving
 *   one of the REPLACE_* outcomes (ConnectionContentTable.helpers)
 * @param {Function} props.renderReplaceConfirmModal ({ oldEntry, newEntry, replacing,
 *   onConfirm, onClose }) => modal JSX
 * @param {Function} [props.rowActions] (row, covered, index) => extra ActionsDropdown actions
 *   for a persisted row, shown between Edit and Remove; `covered` is the row's sync rows
 * @param {string} props.testId table test id
 */
export const ConnectionContentTable = ({
  connectionId = null,
  persistedAllowlist = [],
  syncState,
  title,
  caption,
  columns,
  notice = null,
  locked = false,
  blankRow,
  addLabel,
  entrySchema,
  sameEntry,
  normalizeEntry = (entry) => entry,
  syncRowsFor,
  errorLabels,
  rowLabel,
  onEditingChange = () => {},
  onRemoved,
  draftRemoveLabel,
  persistedRemoveLabel,
  removeTargetOf,
  removeEntry,
  survivesTarget,
  renderRemoveConfirmModal,
  replaceEntry,
  renderReplaceConfirmModal,
  rowActions = () => [],
  testId,
}) => {
  const { values, errors, touched, setFieldValue, setTouched } = useFormikContext()
  const rows = values.allowlist
  // The provider-shaped target of the persisted entry the operator asked to
  // remove; null = no confirmation open.
  const [removeTarget, setRemoveTarget] = useState(null)
  const [removing, setRemoving] = useState(false)
  // The pending persisted-row replacement awaiting confirmation; null = none.
  const [replaceTarget, setReplaceTarget] = useState(null)
  const [replacing, setReplacing] = useState(false)
  // The one row open for inline editing; null = none. A persisted row's edit
  // keeps its typed values HERE, not in Formik — a persisted row's IDENTITY in
  // `values.allowlist` never diverges from server truth, so a Save can never
  // smuggle a new spelling into a plain PATCH with the old entry's documents
  // left ingested. Only confirmed carried values (scope) are written
  // back into the form, and those are exactly what Save is for.
  const [editing, setEditing] = useState(() =>
    // A brand-new form (create mode, or a connection whose entries were all
    // removed) starts with its one blank row open: at least one entry is
    // required, and an open row makes that obvious.
    isEmpty(persistedAllowlist) && rows.length === 1 && sameEntry(rows[0], blankRow)
      ? { index: 0, persisted: false, isNew: true }
      : null
  )
  // How many leading rows are server-backed. Seeded from persistedAllowlist and
  // resynced whenever it changes (a reload), but ALSO adjusted optimistically
  // the instant a removal/replace edits the form — persistedAllowlist (from
  // connection.allowlist) stays stale until the awaited reload lands, and
  // classifying against that stale length during the gap would misclassify
  // rows around the change.
  const [persistedRowCount, setPersistedRowCount] = useState(persistedAllowlist.length)
  useEffect(() => {
    setPersistedRowCount(persistedAllowlist.length)
  }, [persistedAllowlist])

  const isEdit = !isNil(connectionId)
  const isPersistedRow = (index) => isEdit && index < persistedRowCount

  // Own-property check so a reason named like a prototype member
  // ('constructor') still falls back to the raw code instead of resolving a
  // built-in. An unmapped code shows as itself, with no tooltip to add.
  const errorFor = (lastError) =>
    Object.prototype.hasOwnProperty.call(errorLabels, lastError)
      ? errorLabels[lastError]
      : { label: lastError }

  // The columns an entry carries rather than is named by; see `carried` in
  // the props doc.
  const carriedNames = columns.filter((column) => column.carried).map((column) => column.name)

  // Field errors for the persisted edit's local values; Formik never sees
  // them, so the row schema validates directly. `editing.values` is the whole
  // row — identity and carried values alike — so this validates exactly what
  // the confirm will write back or send.
  const persistedEditErrors = (() => {
    if (isNil(editing) || !editing.persisted) {
      return {}
    }
    try {
      entrySchema.validateSync(editing.values, { abortEarly: false })
      return {}
    } catch (validationError) {
      return Object.fromEntries(validationError.inner.map((issue) => [issue.path, issue.message]))
    }
  })()

  // Whether the open persisted edit moved the entry's identity. Compared by
  // normalized SHAPE, not by `sameEntry`: two shapes can share one removal
  // identity (a whole-site entry with and without include_lists) and that
  // edit is real.
  const identityChanged =
    !isNil(editing) &&
    editing.persisted &&
    !isEqual(normalizeEntry({ ...editing.values }), normalizeEntry({ ...editing.original }))

  // The DELETE leg of a replace removes EVERY entry with the old identity, so
  // retyping an entry to match another listed one would fold two entries into
  // one — refuse it up front. Only once the identity has actually moved:
  // duplicate spellings of one identity are a real stored state, and a
  // carried-only edit on one of them keeps the identity it shares, which is
  // not a collision.
  const duplicateOfAnother =
    identityChanged &&
    rows.some((row, index) => index !== editing.index && sameEntry(row, editing.values))

  const hasDraftErrors = (index) =>
    !isString(errors.allowlist) && !isEmpty(errors.allowlist?.[index])

  const startEdit = (index) => {
    if (isPersistedRow(index)) {
      setEditing({
        index,
        persisted: true,
        isNew: false,
        original: rows[index],
        values: { ...rows[index] },
      })
      onEditingChange(true)
      return
    }
    setEditing({ index, persisted: false, isNew: false, original: { ...rows[index] } })
  }

  const confirmPersistedEdit = () => {
    const nextIdentity = normalizeEntry({ ...editing.values })
    const nextCarried = pick(editing.values, carriedNames)
    const carriedChanged = !isEqual(nextCarried, pick(editing.original, carriedNames))
    if (!identityChanged && !carriedChanged) {
      // A no-op "replace" would DELETE the identity it just re-added, so ✓ on
      // unchanged values is a plain cancel instead.
      setEditing(null)
      onEditingChange(false)
      return
    }
    if (!identityChanged) {
      // Only carried values moved: same removal identity, so this is a form
      // change for Save to PATCH, not a replacement. Through the replace flow
      // it would PATCH in a duplicate and then purge both.
      setFieldValue(`allowlist.${editing.index}`, { ...rows[editing.index], ...nextCarried })
      setEditing(null)
      onEditingChange(false)
      return
    }
    // The replacement is the LIVE row with the edited identity and carried
    // values applied on top, not the identity alone. Everything an entry
    // carries that this table does not own — the stored weight, which no column
    // edits — is not part of what is being replaced, and rebuilding from
    // `normalizeEntry` alone silently dropped it. Form-only keys are harmless
    // on the PATCH — the callers convert through their own server projection
    // (`toServerEntry` / `toApiAllowlist`) first.
    //
    // The contract this puts on callers: EVERY key a form row holds must be
    // safe to carry into a replacement. Two consumers see `newEntry` with no
    // server projection in between — the confirm modal and the optimistic row
    // splice — so a client-only key (a cached count, a UI flag, a temp id)
    // would ride into both. Keep that state outside the row.
    setReplaceTarget({
      oldEntry: editing.original,
      newEntry: { ...rows[editing.index], ...nextCarried, ...nextIdentity },
    })
  }

  // During the reload gap after a remove/replace, persistedAllowlist and
  // syncState are one mutually-consistent STALE snapshot while the row set has
  // already been edited. Classification never reads them (isPersistedRow is
  // the tracked count), so the divergence is confined to these read-only
  // cells: at worst a surviving entry under-attributes until the awaited
  // reload lands.
  const syncCellsFor = (row, index) => {
    const persisted = isPersistedRow(index)
    if (!persisted) {
      return {
        documents: null,
        lastSyncedAt: null,
        lastSyncedLabel: 'Not yet synced',
        status: { kind: 'pending' },
        covered: [],
      }
    }
    if (isNil(syncState)) {
      // Loading or failed — the note above the table says which; the cells
      // must not claim "never synced" about state that simply isn't here.
      return {
        documents: null,
        lastSyncedAt: null,
        lastSyncedLabel: '—',
        status: null,
        covered: [],
      }
    }
    const covered = syncRowsFor(row, persistedAllowlist, syncState.rows)
    const lastSyncedAt = aggregateLastSynced(covered)
    return {
      documents: aggregateDocuments(covered),
      lastSyncedAt,
      lastSyncedLabel: isNil(lastSyncedAt)
        ? 'Not yet synced'
        : formatTime(lastSyncedAt, dateFormatMoment.short),
      status: aggregateStatus(covered),
      covered,
    }
  }

  const statusPill = (status) => {
    if (isNil(status)) {
      return null
    }
    if (status.kind === 'error') {
      const info = errorFor(status.code)
      return (
        <Pill critical small tooltip={info.description} data-testid={`${testId}-error`}>
          {info.label}
        </Pill>
      )
    }
    if (status.kind === 'synced') {
      return (
        <Pill success small>
          Synced
        </Pill>
      )
    }
    return (
      <Pill warning small>
        Pending
      </Pill>
    )
  }

  const lastSyncedCell = ({ lastSyncedAt, lastSyncedLabel, covered }) => {
    // One entry can answer for several sync rows (a whole site's libraries
    // plus its pages); the aggregate is the most recent, the tooltip itemizes.
    if (isNil(lastSyncedAt) || covered.length < 2) {
      return lastSyncedLabel
    }
    return (
      <InteractiveTooltip
        content={
          <ul className="connection-content-table__breakdown">
            {covered.map((coveredRow) => (
              <li key={rowLabel(coveredRow)}>
                {rowLabel(coveredRow)} —{' '}
                {isNil(coveredRow.last_synced_at)
                  ? 'Never'
                  : formatTime(coveredRow.last_synced_at, dateFormatMoment.short)}
              </li>
            ))}
          </ul>
        }
      >
        <span className="connection-content-table__synced-detail">{lastSyncedLabel}</span>
      </InteractiveTooltip>
    )
  }

  return (
    <FormField>
      <label>{title}</label>
      <div className="form-caption">{caption}</div>
      {isEdit && isUndefined(syncState) && (
        <div className="field-help small-text">Loading sync state…</div>
      )}
      {isEdit && isNull(syncState) && (
        <div className="field-help small-text">
          Couldn’t load sync state. Close and reopen to retry.
        </div>
      )}
      {notice}
      <FieldArray name="allowlist">
        {(arrayHelpers) => {
          const confirmRemove = async () => {
            setRemoving(true)
            const removed = await removeEntry(removeTarget)
            setRemoving(false)
            if (isNil(removed)) {
              // The thunk already toasted the failure; the allowlist was left
              // unedited server-side, so keep the confirmation open for a retry.
              return
            }
            // The server removes EVERY entry with this identity (duplicate
            // spellings go together), so drop every matching form row too — a
            // surviving sibling row would silently re-add the entry on the
            // next Save. setFieldValue (not repeated arrayHelpers.remove, which
            // computes from one stale values snapshot and would drop only one of
            // N duplicates); realign the parallel touched array by the same
            // predicate so a following draft row's inline errors stay in sync
            // (errors re-derive from the schema on the setFieldValue revalidate).
            const survives = (row) => !survivesTarget(row, removeTarget)
            // Shrink the persisted prefix by the rows being dropped from it now,
            // so classification is correct during the reload gap (not just after).
            setPersistedRowCount(
              (count) =>
                count - values.allowlist.slice(0, count).filter((row) => !survives(row)).length
            )
            if (Array.isArray(touched.allowlist)) {
              setTouched(
                {
                  ...touched,
                  // Array.from densifies the sparse array Formik builds when
                  // only a later row was ever touched — a bare .filter skips
                  // the holes and shifts every following flag left.
                  allowlist: Array.from(touched.allowlist).filter((entry, index) =>
                    survives(values.allowlist[index])
                  ),
                },
                false
              )
            }
            setFieldValue('allowlist', values.allowlist.filter(survives))
            setRemoveTarget(null)
            await onRemoved()
          }

          const confirmReplace = async () => {
            setReplacing(true)
            const outcome = await replaceEntry(replaceTarget)
            setReplacing(false)
            if (outcome === REPLACE_PATCH_FAILED) {
              // Nothing changed server-side; the thunk toasted it. Keep the
              // confirmation open for a retry.
              return
            }
            const { oldEntry, newEntry } = replaceTarget
            if (outcome === REPLACE_REMOVE_FAILED) {
              // The new entry was PATCHed in but the old one's removal failed:
              // the server holds both, consistently — nothing purged, nothing
              // stranded. Mirror it (the new entry appends to the persisted
              // prefix) and leave the old row's Remove as the retry path; the
              // removal thunk's failure toast explains.
              const insertAt = persistedRowCount
              if (Array.isArray(touched.allowlist)) {
                const nextTouched = [...touched.allowlist]
                nextTouched.splice(insertAt, 0, undefined)
                setTouched({ ...touched, allowlist: nextTouched }, false)
              }
              const nextRows = [...values.allowlist]
              nextRows.splice(insertAt, 0, { ...newEntry })
              setPersistedRowCount((count) => count + 1)
              setFieldValue('allowlist', nextRows)
            } else {
              // Replaced: the server now holds the entries minus the old
              // identity, with the new one appended. Same prune the removal
              // does (duplicate spellings go together), then the insert.
              const survives = (row) => !sameEntry(row, oldEntry)
              const keptPersisted = values.allowlist
                .slice(0, persistedRowCount)
                .filter(survives).length
              if (Array.isArray(touched.allowlist)) {
                // Array.from densifies the sparse array Formik builds when
                // only a later row was ever touched — a bare .filter skips
                // the holes and shifts every following flag left.
                const nextTouched = Array.from(touched.allowlist).filter((entry, index) =>
                  survives(values.allowlist[index])
                )
                nextTouched.splice(keptPersisted, 0, undefined)
                setTouched({ ...touched, allowlist: nextTouched }, false)
              }
              const nextRows = values.allowlist.filter(survives)
              nextRows.splice(keptPersisted, 0, { ...newEntry })
              setPersistedRowCount(keptPersisted + 1)
              setFieldValue('allowlist', nextRows)
            }
            setReplaceTarget(null)
            setEditing(null)
            onEditingChange(false)
            await onRemoved()
          }

          const cancelEditing = () => {
            if (editing.persisted) {
              setEditing(null)
              onEditingChange(false)
              return
            }
            if (editing.isNew) {
              // A never-committed row: cancel just removes it.
              arrayHelpers.remove(editing.index)
            } else {
              setFieldValue(`allowlist.${editing.index}`, editing.original)
            }
            setEditing(null)
          }

          const addRow = () => {
            arrayHelpers.push({ ...blankRow })
            setEditing({ index: rows.length, persisted: false, isNew: true })
          }

          const columnPlaceholder = (column, row) =>
            isNil(column.placeholderOf) ? column.placeholder : column.placeholderOf(row)

          // `ariaLabelOf` names the entry the open row's control belongs to,
          // so a screen reader hears which row is being edited rather than a
          // generic "Playbooks this row answers".
          const columnAriaLabel = (column, row) =>
            isNil(column.ariaLabelOf) ? column.ariaLabel : column.ariaLabelOf(row)

          const columnInapplicable = (column, row) =>
            isNil(column.disabledWhen) ? false : column.disabledWhen(row)

          // The one editing control per cell. A persisted row's edit writes the
          // LOCAL editing.values (never Formik — see the `editing` comment); a
          // draft's edit writes form state directly.
          const editingControl = (column, index, row, { hasError, errorId }) => {
            const { persisted } = editing
            // What the control shows and rewrites: the local edit for a
            // persisted row, the form row for a draft.
            const current = persisted ? editing.values : row
            const inapplicable = columnInapplicable(column, current)
            const write = (name, value) => {
              if (persisted) {
                setEditing({ ...editing, values: { ...editing.values, [name]: value } })
                return
              }
              setFieldValue(`allowlist.${index}.${name}`, value)
            }
            if (column.control === 'select') {
              return (
                <div className="connection-content-table__select">
                  <Select
                    options={column.options}
                    value={current[column.name]}
                    isClearable={false}
                    isDisabled={inapplicable}
                    aria-label={columnAriaLabel(column, current)}
                    onChange={(option) => {
                      // `onSelect` replaces the whole row: switching a row's type
                      // can invalidate a sibling field, and clearing that in the
                      // same update keeps the row from ever being submitted in a
                      // shape the server rejects (QCER-1089).
                      if (isNil(column.onSelect)) {
                        write(column.name, option.value)
                        return
                      }
                      if (persisted) {
                        setEditing({
                          ...editing,
                          values: column.onSelect(editing.values, option.value),
                        })
                      } else {
                        setFieldValue(`allowlist.${index}`, column.onSelect(row, option.value))
                      }
                    }}
                  />
                </div>
              )
            }
            if (column.control === 'multiselect') {
              const selected = column.options.filter((option) =>
                current[column.name].includes(option.value)
              )
              return (
                <div className="connection-content-table__select">
                  <Select
                    isMulti
                    // Condense to "N selected" past one value, and never wrap: a
                    // growing control would push the whole table down every time
                    // another value is picked. One still shows its name.
                    maxUnCondensedOptions={1}
                    showFullOptions={false}
                    options={column.options}
                    // A row the value does not apply to shows nothing selected,
                    // whatever it holds: nothing there is in force. Hidden, not
                    // cleared — a scope picked before a folder was typed comes
                    // back if the folder is cleared again, visible here before
                    // anything is confirmed, and the callers' server projection
                    // never sends it while hidden.
                    value={inapplicable ? [] : selected}
                    loading={column.loading}
                    placeholder={columnPlaceholder(column, current)}
                    isDisabled={column.disabled || inapplicable}
                    aria-label={columnAriaLabel(column, current)}
                    onChange={(options) => {
                      const chosen = isNil(options) ? [] : options.map((option) => option.value)
                      // Values with no matching option are INVISIBLE in the
                      // control, so they cannot be part of what the operator just
                      // chose — carry them through instead of dropping them. Two
                      // ways a row gets one: a playbook deleted since the scope
                      // was set, and options that have not finished loading.
                      // Rebuilding from `chosen` alone would silently erase a
                      // scope nobody touched.
                      const unrepresented = current[column.name].filter(
                        (value) => !column.options.some((option) => option.value === value)
                      )
                      write(column.name, [...unrepresented, ...chosen])
                    }}
                  />
                </div>
              )
            }
            const textProps = {
              small: true,
              placeholder: columnPlaceholder(column, current),
              'aria-label': columnAriaLabel(column, current),
              disabled: inapplicable,
              // The message renders as a SIBLING, so the input needs both the
              // critical border and a pointer to it: role="alert" announces
              // once on appearance, and a user tabbing back afterwards would
              // otherwise hear nothing wrong.
              error: hasError,
              'aria-invalid': hasError || undefined,
              'aria-describedby': hasError ? errorId : undefined,
            }
            if (persisted) {
              return (
                <TextInput
                  {...textProps}
                  value={editing.values[column.name]}
                  onChange={(event) => write(column.name, event.target.value)}
                />
              )
            }
            return (
              <Field as={TextInput} {...textProps} name={`allowlist.${index}.${column.name}`} />
            )
          }

          // Names the selected options. A value with no option — its playbook
          // deleted since, or every value while the options are still loading
          // — is counted rather than dropped, so the cell never understates
          // the stored scope: "Adventist +1", or "2 selected" when none
          // resolve. The blank label would claim "every" of something the row
          // actually restricts.
          const multiselectLabel = (column, row) => {
            const selected = row[column.name]
            if (isEmpty(selected)) {
              return <span className="muted-text">{column.blankLabel}</span>
            }
            const names = column.options
              .filter((option) => selected.includes(option.value))
              .map((option) => option.label)
            if (isEmpty(names)) {
              return `${selected.length} selected`
            }
            const unnamed = selected.length - names.length
            return unnamed === 0 ? names.join(', ') : `${names.join(', ')} +${unnamed}`
          }

          // A read-only cell. A value the row cannot carry (`disabledWhen`)
          // shows as a dash whatever the row holds, so nothing is presented as
          // in force that is not.
          const displayValue = (column, row) => {
            if (columnInapplicable(column, row)) {
              return <span className="muted-text">—</span>
            }
            if (column.control === 'select') {
              // Display the selected option's LABEL — the value is a machine
              // token the operator never typed.
              const selected = column.options.find((option) => option.value === row[column.name])
              return isNil(selected) ? row[column.name] : selected.label
            }
            if (column.control === 'multiselect') {
              return multiselectLabel(column, row)
            }
            if (isEmpty(row[column.name].trim()) && !isNil(column.blankLabel)) {
              return <span className="muted-text">{column.blankLabel}</span>
            }
            return row[column.name]
          }

          const identityCells = (row, index, isEditingRow) =>
            columns.map((column, columnIndex) => {
              if (isEditingRow) {
                // A persisted edit validates its local values; a draft shows
                // Formik's error once the field was touched.
                const error = editing.persisted
                  ? persistedEditErrors[column.name]
                  : touched.allowlist?.[index]?.[column.name] &&
                    errors.allowlist?.[index]?.[column.name]
                const hasError = Boolean(column.showError) && isString(error)
                const errorId = `allowlist-${index}-${column.name}-error`
                return (
                  <td key={column.name}>
                    {editingControl(column, index, row, { hasError, errorId })}
                    {hasError && <ErrorMessage id={errorId} content={error} />}
                    {editing.persisted && columnIndex === 0 && duplicateOfAnother && (
                      <ErrorMessage content="Already listed" />
                    )}
                  </td>
                )
              }
              // A STORED value that blocks Save explains itself on the read-only
              // cell, whether or not the operator has touched anything: they
              // have to know which row to open. (`isString` guards the shape
              // hasDraftErrors guards: a failed `.min(1)` puts a string on
              // errors.allowlist, and indexing that by a column name would
              // render one character as an error.)
              const storedError =
                column.showError && !isString(errors.allowlist)
                  ? errors.allowlist?.[index]?.[column.name]
                  : undefined
              const note = isNil(column.note)
                ? null
                : column.note(row, { persisted: isPersistedRow(index) })
              return (
                <td key={column.name}>
                  {displayValue(column, row)}
                  {isString(storedError) && <ErrorMessage content={storedError} />}
                  {!isNil(note) && <div className="connection-content-table__note">{note}</div>}
                </td>
              )
            })

          const actionsCell = (row, index, isEditingRow, covered) => {
            if (isEditingRow) {
              const confirmDisabled = editing.persisted
                ? !isEmpty(persistedEditErrors) || duplicateOfAnother
                : hasDraftErrors(index)
              return (
                <div className="flex-align-center small-gap">
                  <Button
                    type="button"
                    small
                    square
                    brand
                    aria-label="Save entry"
                    disabled={confirmDisabled}
                    onClick={() => (editing.persisted ? confirmPersistedEdit() : setEditing(null))}
                  >
                    <IconCheck />
                  </Button>
                  <Button
                    type="button"
                    small
                    square
                    basic
                    aria-label="Cancel editing"
                    onClick={cancelEditing}
                  >
                    <IconX />
                  </Button>
                </div>
              )
            }
            const persisted = isPersistedRow(index)
            return (
              <ActionsDropdown
                dataTestId={`${testId}-row-actions-${index}`}
                buttonProps={{
                  basic: true,
                  disabled: !isNil(editing) || locked,
                  'aria-label': 'Row actions',
                }}
                actions={[
                  {
                    label: 'Edit',
                    onClick: () => startEdit(index),
                    testId: `${testId}-edit-${index}`,
                  },
                  // A draft has ingested nothing, so only saved rows get the provider's actions.
                  ...(persisted ? rowActions(row, covered, index) : []),
                  {
                    label: persisted ? persistedRemoveLabel(row) : draftRemoveLabel,
                    isDanger: true,
                    onClick: () =>
                      persisted ? setRemoveTarget(removeTargetOf(row)) : arrayHelpers.remove(index),
                    testId: `${testId}-remove-${index}`,
                  },
                ]}
              />
            )
          }

          return (
            <>
              <div className="connection-content-table__frame">
                <div className="table-scroll">
                  <Table compact celled className="connection-content-table" data-testid={testId}>
                    <thead>
                      <tr>
                        {columns.map((column) => (
                          <th key={column.name}>{column.header}</th>
                        ))}
                        <th className="collapsing">Documents</th>
                        <th className="collapsing">Last synced</th>
                        <th className="collapsing">Status</th>
                        <th className="collapsing sticky-action" aria-label="Actions" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((row, index) => {
                        const isEditingRow = !isNil(editing) && editing.index === index
                        const cells = syncCellsFor(row, index)
                        return (
                          // eslint-disable-next-line react/no-array-index-key
                          <tr key={index} className={classNames({ 'is-editing': isEditingRow })}>
                            {identityCells(row, index, isEditingRow)}
                            <td className="collapsing">
                              {isNil(cells.documents) ? '—' : cells.documents}
                            </td>
                            <td className="collapsing">{lastSyncedCell(cells)}</td>
                            <td className="collapsing">{statusPill(cells.status)}</td>
                            <td className="collapsing sticky-action connection-content-table__actions">
                              {actionsCell(row, index, isEditingRow, cells.covered)}
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </Table>
                </div>
                {/* Outside the scroll container on purpose: the add action stays
                  visible and centered even while the table scrolls, and it
                  carries the table's bottom edge. */}
                <Button
                  type="button"
                  fullWidth
                  className="connection-content-table__add"
                  icon={<IconPlus />}
                  disabled={!isNil(editing) || locked}
                  onClick={addRow}
                  data-testid={`${testId}-add`}
                >
                  {addLabel}
                </Button>
              </div>
              {isEdit && !isNil(syncState) && !isNil(syncState.updated_at) && (
                <div className="field-help small-text">
                  {/* Persisted state, not live: label it by its own timestamp. */}
                  State as of {formatTime(syncState.updated_at)}
                  {isNil(syncState.mode) ? '' : ` (${syncState.mode} run)`}
                </div>
              )}
              {/* Array-level error (e.g. the min(1) requirement) is a string on
                  errors.allowlist; per-row errors are objects. */}
              {isString(errors.allowlist) && <ErrorMessage content={errors.allowlist} />}
              {!isNil(removeTarget) &&
                renderRemoveConfirmModal({
                  target: removeTarget,
                  removing,
                  onConfirm: confirmRemove,
                  onClose: () => {
                    if (!removing) {
                      setRemoveTarget(null)
                    }
                  },
                })}
              {!isNil(replaceTarget) &&
                renderReplaceConfirmModal({
                  oldEntry: replaceTarget.oldEntry,
                  newEntry: replaceTarget.newEntry,
                  replacing,
                  onConfirm: confirmReplace,
                  onClose: () => {
                    // Back to the still-open editing row, values intact.
                    if (!replacing) {
                      setReplaceTarget(null)
                    }
                  },
                })}
            </>
          )
        }}
      </FieldArray>
    </FormField>
  )
}
